/**
 * N3 · Competência 3 (Geometria) · task-model H3.17.K01
 *
 *   "Reconhecer, entre figuras de seis quadrados, as que são planificações do cubo."
 *
 * Seis figuras, cada uma com seis quadrados ligados pelos lados. O aluno marca TODAS as
 * que, dobradas, fecham um cubo. Duas a quatro fecham; as outras não — e cada uma das
 * que não fecham tem um motivo, que é o nome do erro de quem a aceita:
 *  - um bloco 2 × 2 (dois quadrados cairiam na mesma face);
 *  - cinco quadrados em fila (o quinto cai sobre o primeiro);
 *  - abas do mesmo lado: sem bloco nem fila longa, e ainda assim duas faces se
 *    sobrepõem — a figura que mais parece planificação sem ser.
 *
 * Entre as que fecham, sempre há uma da família da cruz (1-4-1) e uma de outra família
 * (2-3-1, a escada 2-2-2 ou as duas filas 3-3): o erro de rejeitar planificação que
 * não é cruz também tem nome.
 *
 * A validade de cada figura NÃO é tabelada à mão: sai de rolar um cubo sobre ela (ver
 * `_comum/cubo.ts`), e um teste confere que o rolamento acha as 11 planificações entre
 * os 35 hexaminós.
 */

import type { Rng } from '../../core/rng.ts';
import { capitalizar, lista, numeroDe, oS, osN, verbo } from '../../core/pt.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  CUBO_BLOCO_2X2,
  CUBO_CINCO_EM_LINHA,
  CUBO_FACES_SOBREPOSTAS,
  CUBO_REJEITOU_VALIDA,
} from '../../schema/errors.ts';
import type { ItemTemplate, Regiao } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  altura,
  familia,
  fechaCubo,
  hexaminos,
  largura,
  maiorFila,
  motivo,
  N_TRANSFORMACOES,
  transformar,
  type Celula,
  type Familia,
  type Motivo,
} from '../_comum/cubo.ts';

export interface Painel {
  readonly celulas: readonly Celula[];
  readonly familia: Familia | null;
  readonly motivo: Motivo | null;
}

export interface Params {
  readonly paineis: readonly Painel[];
}

const LETRAS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

/** Lado do quadradinho e área de cada painel: 6 × 3 quadradinhos, com margem. */
const Q = 10;
const PW = 7 * Q;
const PH = 4 * Q;
const GX = Q;
const GY = 1.8 * Q;

function embaralhar<T>(rng: Rng, xs: readonly T[]): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

/** Deita a figura (mais larga que alta), numa das orientações possíveis. */
function deitar(rng: Rng, p: readonly Celula[]): Celula[] {
  const opcoes: Celula[][] = [];
  for (let k = 0; k < N_TRANSFORMACOES; k++) {
    const q = transformar(p, k);
    if (largura(q) >= altura(q) && largura(q) <= 6 && altura(q) <= 3) opcoes.push(q);
  }
  return rng.pick(opcoes);
}

function drawParams(rng: Rng): Params {
  const todas = hexaminos();
  const validas = todas.filter(fechaCubo);
  const invalidas = todas.filter((p) => !fechaCubo(p));
  const cruz = embaralhar(
    rng.fork('cruz'),
    validas.filter((p) => familia(p) === '1-4-1'),
  );
  const outras = embaralhar(
    rng.fork('outras'),
    validas.filter((p) => familia(p) !== '1-4-1'),
  );
  const k = rng.pick([2, 3, 3, 4]);
  const resto = embaralhar(rng.fork('resto'), [...cruz.slice(1), ...outras.slice(1)]);
  const certas = [cruz[0], outras[0], ...resto.slice(0, k - 2)] as (readonly Celula[])[];

  const porMotivo = (m: Motivo): (readonly Celula[])[] =>
    embaralhar(
      rng.fork(m),
      invalidas.filter((p) => motivo(p) === m),
    );
  const sobrepoe = porMotivo('sobrepoe');
  const outraFalha = rng.bool() ? porMotivo('bloco') : porMotivo('linha');
  const erradas = [sobrepoe[0], outraFalha[0]] as (readonly Celula[])[];
  for (const p of embaralhar(rng.fork('mais'), invalidas)) {
    if (erradas.length >= 6 - k) break;
    if (!erradas.includes(p)) erradas.push(p);
  }

  const paineis = embaralhar(rng.fork('ordem'), [...certas, ...erradas]).map((p) => {
    const celulas = deitar(rng.fork('giro'), p);
    return { celulas, familia: familia(celulas), motivo: motivo(celulas) };
  });
  return { paineis };
}

const MOTIVO_TXT: Readonly<Record<Motivo, (p: Painel) => string>> = {
  bloco: () =>
    'há quatro quadrados formando um quadrado 2 × 2, e ao dobrar dois deles caem na mesma face',
  linha: (p) =>
    `há ${maiorFila(p.celulas)} quadrados em fila; quatro já dão a volta no cubo, e o quinto cai sobre o primeiro`,
  sobrepoe: () => 'ao dobrar, dois quadrados caem na mesma face, e outra face do cubo fica aberta',
};

/** Canto superior esquerdo do painel i (y para cima). */
function canto(i: number): Vec2 {
  const col = i % 2;
  const lin = Math.floor(i / 2);
  return { x: col * (PW + GX), y: -lin * (PH + GY) };
}

export const planificacoesCubo: ItemTemplate<Params, null> = {
  id: templateId('h3-17-K01-planificacoes-cubo'),
  version: 1,
  taskModel: taskModelCode('H3.17.K01'),
  skills: [skillCode('H3.17'), skillCode('H1.17')],
  axis: 'I',
  level: 'N3',
  title: 'Planificações do cubo',
  responseModel: '2PL',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.paineis.some((x) => x.familia === '2-2-2' || x.familia === '3-3'))
      fontes.push('steps.3plus');
    if (p.paineis.filter((x) => x.familia !== null).length >= 4)
      fontes.push('distractor-proximity.high');
    return fontes;
  },
  build(p) {
    const backdrop: Shape[] = [];
    const regioes: Regiao[] = [];
    p.paineis.forEach((painel, i) => {
      const c = canto(i);
      const letra = LETRAS[i] as string;
      const w = largura(painel.celulas);
      const h = altura(painel.celulas);
      const ox = c.x + (PW - w * Q) / 2;
      const oy = c.y - (PH - h * Q) / 2;
      backdrop.push({
        t: 'rect',
        at: { x: c.x, y: c.y - PH, w: PW, h: PH },
        role: 'auxiliary',
        fill: 'surface',
      });
      for (const [x, y] of painel.celulas) {
        backdrop.push({
          t: 'rect',
          at: { x: ox + x * Q, y: oy - (y + 1) * Q, w: Q, h: Q },
          role: 'given',
          fill: 'given-fill',
        });
      }
      backdrop.push({
        t: 'label',
        at: { x: c.x + PW / 2, y: c.y },
        text: [strong(letra)],
        anchor: 'n',
      });
      regioes.push({
        id: letra,
        rotulo: letra,
        nome: `Figura ${letra}`,
        forma: [
          { x: c.x, y: c.y },
          { x: c.x + PW, y: c.y },
          { x: c.x + PW, y: c.y - PH },
          { x: c.x, y: c.y - PH },
        ],
      });
    });

    const chave = p.paineis.flatMap((x, i) => (x.familia !== null ? [LETRAS[i] as string] : []));
    const erradas = p.paineis.flatMap((x, i) => (x.motivo !== null ? [LETRAS[i] as string] : []));
    const motivoDe = (letra: string): Motivo | null =>
      p.paineis[LETRAS.indexOf(letra as (typeof LETRAS)[number])]?.motivo ?? null;
    const aceitou = (m: Motivo) => (sel: readonly string[]) => sel.some((l) => motivoDe(l) === m);

    const analisar = (sel: readonly string[]): RichText => {
      const partes: string[] = [];
      p.paineis.forEach((painel, i) => {
        const l = LETRAS[i] as string;
        if (painel.motivo !== null && sel.includes(l))
          partes.push(`A figura ${l} não fecha o cubo: ${MOTIVO_TXT[painel.motivo](painel)}.`);
        if (painel.familia !== null && !sel.includes(l))
          partes.push(`A figura ${l} fecha o cubo, e ficou sem marcar.`);
      });
      return [txt(partes.join(' '))];
    };

    const descricao =
      'Seis figuras, de A a F, em duas colunas; cada uma é formada por seis quadrados ligados pelos lados. ' +
      p.paineis
        .map(
          (x, i) =>
            `Figura ${LETRAS[i]}: quadrados nas posições ${x.celulas.map(([cx, cy]) => `(${cx + 1}, ${cy + 1})`).join(', ')}, contando colunas da esquerda e linhas de cima.`,
        )
        .join(' ');

    /** As figuras que não fecham, reunidas pela explicação que as descreve. */
    const porMotivo = (letras: readonly string[]): { letras: string[]; texto: string }[] => {
      const grupos = new Map<string, string[]>();
      for (const l of letras) {
        const m = motivoDe(l) as Motivo;
        const texto = MOTIVO_TXT[m](
          p.paineis[LETRAS.indexOf(l as (typeof LETRAS)[number])] as Painel,
        );
        grupos.set(texto, [...(grupos.get(texto) ?? []), l]);
      }
      return [...grupos].map(([texto, ls]) => ({ letras: ls, texto }));
    };

    return {
      stem: [
        txt(
          'Quando se desmonta um cubo de papel e se abre sobre a mesa, as seis faces viram seis quadrados ligados pelos lados: é uma ',
        ),
        strong('planificação'),
        txt(' do cubo.'),
      ],
      comando: [txt('Clique em todas as figuras que são planificações de um cubo.')],
      stimuli: [],
      interaction: {
        kind: 'region-select',
        descricao,
        backdrop,
        viewBox: {
          x: -4,
          y: -(2 * (PH + GY) + PH) - 4,
          w: 2 * PW + GX + 8,
          h: 2 * (PH + GY) + PH + 4 + 12,
        },
        regioes,
        oQue: 'figura',
        chave,
        erros: [],
        diagnosticos: [
          { error: CUBO_BLOCO_2X2, test: aceitou('bloco') },
          { error: CUBO_CINCO_EM_LINHA, test: aceitou('linha') },
          { error: CUBO_FACES_SOBREPOSTAS, test: aceitou('sobrepoe') },
          { error: CUBO_REJEITOU_VALIDA, test: (sel) => chave.some((l) => !sel.includes(l)) },
        ],
        analisar,
      },
      dicas: [
        [
          txt(
            'Em cada figura, escolha um quadrado para ser o fundo do cubo e imagine os vizinhos dobrando para cima.',
          ),
        ],
        [
          txt(
            'Quatro quadrados formando um quadrado 2 × 2 nunca fecham um cubo; cinco quadrados em fila também não.',
          ),
        ],
        [
          txt(
            'Numa fila de quatro, que dá a volta no cubo, os dois quadrados que sobram precisam ficar um de cada lado da ' +
              'fila — nunca os dois do mesmo lado.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(`Fecham o cubo as figuras ${lista(chave)}. `),
        txt(
          'Para conferir, escolha um quadrado para ser a base e dobre os vizinhos para cima, um de cada vez: cada quadrado precisa cair numa face diferente do cubo.',
        ),
        br,
        // Agrupadas pelo motivo: duas figuras que falham pela mesma razão ganhavam a
        // mesma frase inteira, repetida uma depois da outra.
        txt(
          porMotivo(erradas)
            .map(({ letras, texto }) => {
              const figuras = numeroDe(letras.length) === 'sg' ? oS('figura') : osN('figura');
              // "em cada uma" quando são várias: o motivo descreve UMA figura.
              const cada = figuras.n === 'pl' ? 'em cada uma, ' : '';
              return `${capitalizar(figuras)} ${lista(letras)} não ${verbo('fechar.pres', figuras.n)}: ${cada}${texto}.`;
            })
            .join(' '),
        ),
      ],
      declared: p.paineis.some((x) => x.familia === '2-2-2' || x.familia === '3-3')
        ? 'dificil'
        : 'media',
    };
  },
};
